import Konva from 'konva';
import type { ComponentDef } from '../../core/library/component-def.js';
import { addUnifiedValueLabel } from './value-label.js';

/**
 * Renderer del cuerpo de un IC en encapsulado DIP.
 * Devuelve un Konva.Group en coordenadas locales al componente (mm).
 *
 * Elementos:
 *  - Rectángulo con esquinas ligeramente redondeadas.
 *  - Degradado horizontal suave (oscuro-claro-oscuro) para dar volumen.
 *  - Muesca semicircular en el lado izquierdo (indica orientación).
 *  - Punto pequeño junto a pin 1 (esquina superior izquierda en las
 *    coordenadas locales, es decir, cerca de x=-length/2, y=+width/2).
 *  - Etiqueta central con el texto (part number).
 */
export function renderDipBody(
  def: ComponentDef,
  labelText: string,
  flipped: boolean = false,
  rotationDeg: number = 0,
): Konva.Group {
  const g = new Konva.Group();

  // length_mm = ancho horizontal (X); width_mm = alto vertical (Y).
  // Para nuestro DIP con pines a los lados, el cuerpo queda vertical
  // (width_mm suele ser mayor que length_mm).
  const length = def.body.length_mm ?? 7.0;
  const width = def.body.width_mm ?? 9.4;
  // Redondeo mínimo del ~8 % del lado menor para que las esquinas
  // se lean claramente como redondeadas, salvo que el JSON pida más.
  const cornerRadius = Math.max(
    def.body.corner_radius_mm ?? 0.4,
    Math.min(length, width) * 0.08,
  );

  const bodyColor = def.appearance.body_color;
  const light = mix(bodyColor, '#ffffff', 0.28);
  const veryLight = mix(bodyColor, '#ffffff', 0.55);
  const dark = mix(bodyColor, '#000000', 0.45);
  const veryDark = mix(bodyColor, '#000000', 0.65);

  // Cuerpo principal SIN relieve: color plano ligeramente degradado.
  // Cuando el IC no está invertido añadimos encima 4 chaflanes que dan
  // el aspecto de bisel característico del encapsulado DIP; al invertir
  // sólo queda el cuerpo plano (ese lado no tiene relieve).
  // Perímetro: color plano (más claro que antes, para que sirva de
  // "borde de plástico" y no corte visualmente el degradado del bisel).
  // Cuando el IC está invertido rellena con `dark` uniforme (sin relieve).
  const rimColor = mix(bodyColor, '#ffffff', 0.05);
  const outlineColor = '#000000';
  const outlineWidth = 0.12;

  // Grupo con clip a la silueta redondeada del cuerpo: así los
  // chaflanes (polígonos rectos) no sobresalen de las esquinas.
  const bodyGroup = new Konva.Group({
    clipFunc: (ctx) => {
      const x = -length / 2, y = -width / 2, w = length, h = width;
      const r = Math.min(cornerRadius, w / 2, h / 2);
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.lineTo(x + w - r, y);
      ctx.quadraticCurveTo(x + w, y, x + w, y + r);
      ctx.lineTo(x + w, y + h - r);
      ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
      ctx.lineTo(x + r, y + h);
      ctx.quadraticCurveTo(x, y + h, x, y + h - r);
      ctx.lineTo(x, y + r);
      ctx.quadraticCurveTo(x, y, x + r, y);
      ctx.closePath();
    },
  });

  // Relleno base (dentro del clip). Sin stroke: el contorno se
  // dibuja al final fuera del clip para que quede nítido.
  const rect = new Konva.Rect({
    x: -length / 2,
    y: -width / 2,
    width: length,
    height: width,
    cornerRadius,
    fill: flipped ? dark : rimColor,
    listening: true,
  });
  rect.setAttr('nodeKind', 'body');
  bodyGroup.add(rect);
  g.add(bodyGroup);

  if (!flipped) {
    // Cuatro chaflanes formando un marco biselado. Cada chaflán es un
    // trapecio con degradado del exterior (tono medio, empalma con el
    // rim) al interior (más claro), simulando la faceta inclinada.
    const hL = length / 2;
    const hW = width / 2;
    const ch = Math.min(length, width) * 0.12;
    const iL = hL - ch;
    const iW = hW - ch;
    const bevelOuter = mix(bodyColor, '#ffffff', 0.12);

    // Iluminación coherente en TODO el cuerpo desde arriba hacia
    // abajo: arriba oscuro, abajo claro. Los 4 chaflanes toman el
    // gradiente vertical global; sus tonos difieren según la
    // inclinación de la faceta.
    const bodyGradStops = [0, bevelOuter, 0.5, light, 1, veryLight];

    // Top chamfer (faceta que "mira" hacia arriba — más oscura).
    bodyGroup.add(new Konva.Line({
      points: [-hL, -hW, hL, -hW, iL, -iW, -iL, -iW],
      closed: true,
      fillLinearGradientStartPoint: { x: 0, y: -hW },
      fillLinearGradientEndPoint: { x: 0, y: hW },
      fillLinearGradientColorStops: bodyGradStops,
      stroke: outlineColor,
      strokeWidth: outlineWidth,
      lineJoin: 'miter',
      listening: false,
    }));
    // Bottom chamfer (faceta que "mira" hacia abajo — recibe más luz).
    bodyGroup.add(new Konva.Line({
      points: [-hL, hW, -iL, iW, iL, iW, hL, hW],
      closed: true,
      fillLinearGradientStartPoint: { x: 0, y: -hW },
      fillLinearGradientEndPoint: { x: 0, y: hW },
      fillLinearGradientColorStops: bodyGradStops,
      stroke: outlineColor,
      strokeWidth: outlineWidth,
      lineJoin: 'miter',
      listening: false,
    }));
    // Left chamfer
    bodyGroup.add(new Konva.Line({
      points: [-hL, -hW, -iL, -iW, -iL, iW, -hL, hW],
      closed: true,
      fillLinearGradientStartPoint: { x: 0, y: -hW },
      fillLinearGradientEndPoint: { x: 0, y: hW },
      fillLinearGradientColorStops: bodyGradStops,
      stroke: outlineColor,
      strokeWidth: outlineWidth,
      lineJoin: 'miter',
      listening: false,
    }));
    // Right chamfer
    bodyGroup.add(new Konva.Line({
      points: [hL, -hW, hL, hW, iL, iW, iL, -iW],
      closed: true,
      fillLinearGradientStartPoint: { x: 0, y: -hW },
      fillLinearGradientEndPoint: { x: 0, y: hW },
      fillLinearGradientColorStops: bodyGradStops,
      stroke: outlineColor,
      strokeWidth: outlineWidth,
      lineJoin: 'miter',
      listening: false,
    }));

    // Cara superior (rectángulo interior). Mismo degradado vertical
    // que los chaflanes: cielo oscuro arriba, luz abajo.
    bodyGroup.add(new Konva.Rect({
      x: -iL,
      y: -iW,
      width: 2 * iL,
      height: 2 * iW,
      cornerRadius: cornerRadius * 0.5,
      fillLinearGradientStartPoint: { x: 0, y: -hW },
      fillLinearGradientEndPoint: { x: 0, y: hW },
      fillLinearGradientColorStops: [0, mix(bodyColor, '#000000', 0.55), 0.5, mix(bodyColor, '#000000', 0.35), 1, mix(bodyColor, '#000000', 0.1)],
      stroke: outlineColor,
      strokeWidth: outlineWidth,
      listening: false,
    }));

    // Muesca semicircular arriba (indicador de orientación).
    const notchRadius = Math.min(length * 0.14, 0.9);
    bodyGroup.add(new Konva.Arc({
      x: 0,
      y: -iW,
      innerRadius: 0,
      outerRadius: notchRadius,
      angle: 180,
      rotation: 0,
      fillRadialGradientStartPoint: { x: 0, y: -notchRadius * 0.3 },
      fillRadialGradientEndPoint: { x: 0, y: 0 },
      fillRadialGradientStartRadius: 0,
      fillRadialGradientEndRadius: notchRadius,
      fillRadialGradientColorStops: [0, veryDark, 1, dark],
      stroke: veryDark,
      strokeWidth: 0.05,
      listening: false,
    }));

    // Punto de pin 1 (mantenido aunque no aparezca en la referencia).
    const dotOffset = Math.min(width, length) * 0.14;
    bodyGroup.add(new Konva.Circle({
      x: -iL + dotOffset,
      y: -iW + dotOffset,
      radius: Math.min(width, length) * 0.055,
      fillRadialGradientStartPoint: { x: -0.05, y: -0.05 },
      fillRadialGradientEndPoint: { x: 0, y: 0 },
      fillRadialGradientStartRadius: 0,
      fillRadialGradientEndRadius: Math.min(width, length) * 0.055,
      fillRadialGradientColorStops: [0, dark, 1, veryDark],
      stroke: veryDark,
      strokeWidth: 0.04,
      listening: false,
    }));
  }

  // Contorno perimetral nítido, dibujado FUERA del clip para que se
  // vea completo (el clip cortaría la mitad exterior del trazo).
  g.add(new Konva.Rect({
    x: -length / 2,
    y: -width / 2,
    width: length,
    height: width,
    cornerRadius,
    stroke: outlineColor,
    strokeWidth: outlineWidth,
    listening: false,
  }));

  // Etiqueta (part number). Envuelta en grupo con contra-transformación
  // para que siempre aparezca a 0° world y sin invertir, independientemente
  // de la rotación y el flag flipped del componente.
  const ps = flipped ? -1 : 1;
  const labelGroup = new Konva.Group({
    rotation: ps * (0 - rotationDeg),
    scaleY: ps,
    listening: false,
  });
  const fontSize = def.appearance.label?.font_size_mm ?? 1.5;
  const textBox = Math.max(length, width) * 0.95;
  addUnifiedValueLabel(labelGroup, labelText, fontSize, textBox);
  g.add(labelGroup);

  return g;
}

function mix(a: string, b: string, t: number): string {
  const ca = parseHex(a);
  const cb = parseHex(b);
  return `#${toHex(ca.r + (cb.r - ca.r) * t)}${toHex(ca.g + (cb.g - ca.g) * t)}${toHex(ca.b + (cb.b - ca.b) * t)}`;
}
function parseHex(hex: string): { r: number; g: number; b: number } {
  let s = hex.replace('#', '');
  if (s.length === 3) s = s.split('').map((c) => c + c).join('');
  return {
    r: parseInt(s.slice(0, 2), 16),
    g: parseInt(s.slice(2, 4), 16),
    b: parseInt(s.slice(4, 6), 16),
  };
}
function toHex(n: number): string {
  const v = Math.round(Math.max(0, Math.min(255, n)));
  return v.toString(16).padStart(2, '0');
}
